import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { 
  ClipboardList, Calendar, TrendingUp, StickyNote,
  ChevronRight
} from "lucide-react";

// Import existing pages as components
import LifeTasksPage from "./LifeTasksPage";
import CalendarPage from "./CalendarPage";
import ExpensesPage from "./ExpensesPage";
import NotesPage from "./NotesPage";

const SECTIONS = [
  { id: "compiti", label: "Compiti", icon: ClipboardList, description: "I tuoi task organizzati per area", color: "from-indigo-500 to-blue-600" },
  { id: "calendario", label: "Calendario e Promemoria", icon: Calendar, description: "Appuntamenti e scadenze", color: "from-blue-500 to-cyan-600" },
  { id: "spese", label: "Spese", icon: TrendingUp, description: "Entrate, uscite e budget", color: "from-emerald-500 to-teal-600" },
  { id: "note", label: "Note", icon: StickyNote, description: "Appunti veloci e idee", color: "from-amber-500 to-orange-600" },
];

const OrganizzaPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeSection, setActiveSection] = useState(null);

  useEffect(() => {
    const section = searchParams.get("section");
    if (section) {
      setActiveSection(section);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // If a section is active, show that page
  if (activeSection) {
    const BackButton = () => (
      <Button 
        variant="ghost" 
        size="sm" 
        onClick={() => setActiveSection(null)}
        className="mb-4"
      >
        ← Torna a Organizza
      </Button>
    );

    switch (activeSection) {
      case "compiti":
        return (
          <div>
            <BackButton />
            <LifeTasksPage />
          </div>
        );
      case "calendario":
        return (
          <div>
            <BackButton />
            <CalendarPage />
          </div>
        );
      case "spese":
        return (
          <div>
            <BackButton />
            <ExpensesPage />
          </div>
        );
      case "note":
        return (
          <div>
            <BackButton />
            <NotesPage />
          </div>
        );
      default:
        return null;
    }
  }

  // Hub view
  return (
    <div className="space-y-5 animate-fade-in pb-24 md:pb-8" data-testid="organizza-page">
      {/* Header */}
      <div>
        <h1 className="page-title">Organizza</h1>
        <p className="text-muted-foreground">
          Task, appuntamenti, soldi e appunti in un unico posto
        </p>
      </div>

      {/* Section Cards */}
      <div className="grid gap-3 sm:grid-cols-2">
        {SECTIONS.map(({ id, label, icon: Icon, description, color }) => (
          <Card 
            key={id}
            className="border shadow-sm hover:shadow-md transition-all cursor-pointer group overflow-hidden"
            onClick={() => setActiveSection(id)}
          >
            <CardContent className="p-0">
              <div className="flex items-stretch">
                {/* Color bar */}
                <div className={`w-1.5 bg-gradient-to-b ${color}`} />
                
                <div className="flex-1 p-4 flex items-center gap-4">
                  <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shrink-0 group-hover:scale-105 transition-transform`}>
                    <Icon className="w-5 h-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold flex items-center gap-1">
                      {label}
                      <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                    </h3>
                    <p className="text-sm text-muted-foreground truncate">{description}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default OrganizzaPage;
